import { Customer, PaymentMethod } from "@/core";
import { StatusTier } from "@/core/dtos/status-tier";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

export type CustomerPortalAnalyticsEventName =
  | "offer_view"
  | "deal_add_payout_method"
  | "offer_view_alternative"
  | "deal_approve_offer"
  | "deal_submitted"
  | "final_deal_submitted"; 

export type CustomerPortalAnalyticsDealCondition =
  | "under_minimum"
  | "over_maximum"
  | "rejected";

interface AnalyticsProfileFallback {
  email?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
}

const PENDING_DEAL_SUBMITTED_PAYLOAD_STORAGE_KEY =
  "customer-portal.pending-deal-submitted-payload";

export interface CustomerPortalAnalyticsPayload {
  event: CustomerPortalAnalyticsEventName;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  offer_type: "buy" | "sell" | null;
  miles_points_type: string | null;
  amount: number | null;
  amount_of_miles: number | null;
  currency: "USD";
  payout_method: "PayPal" | "Wire" | null;
  region: string | null;
  bonus_tier: string | null;
  deal_condition?: CustomerPortalAnalyticsDealCondition | null;
  deal_status?: "successful" | "rejected" | null;
}

function normalizeAnalyticsText({
  value,
}: {
  value: unknown;
}): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const normalizedValue = value.trim();
  return normalizedValue.length > 0 ? normalizedValue : null;
}

function normalizeAnalyticsEmail({
  value,
}: {
  value: unknown;
}): string | null {
  const normalizedValue = normalizeAnalyticsText({ value });
  return normalizedValue ? normalizedValue.toLowerCase() : null;
}

function normalizeAnalyticsPhoneNumber({
  value,
}: {
  value: unknown;
}): string | null {
  const normalizedValue = normalizeAnalyticsText({ value });
  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue.startsWith("+")) {
    const digitsOnly = normalizedValue.replace(/[^\d]/g, "");
    return digitsOnly ? `+${digitsOnly}` : null;
  }

  const digitsOnly = normalizedValue.replace(/\D/g, "");
  return digitsOnly || null;
}

function normalizeAnalyticsNumber({
  value,
}: {
  value: unknown;
}): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const normalizedValue = value.replace(/,/g, "").trim();
    if (!normalizedValue) {
      return null;
    }

    const numericValue = Number(normalizedValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  return null;
}

function normalizeAnalyticsOfferType({
  value,
}: {
  value: unknown;
}): "buy" | "sell" | null {
  const normalizedValue = normalizeAnalyticsText({ value })?.toLowerCase();

  if (!normalizedValue) {
    return null;
  }

  if (normalizedValue === "buy" || normalizedValue === "sales") {
    return "buy";
  }

  if (normalizedValue === "sell" || normalizedValue === "purchase") {
    return "sell";
  }

  return null;
}

function normalizeAnalyticsPayoutMethod({
  value,
}: {
  value: unknown;
}): "PayPal" | "Wire" | null {
  if (value === PaymentMethod.PAYPAL) {
    return "PayPal";
  }

  if (value === PaymentMethod.WIRE) {
    return "Wire";
  }

  const normalizedValue = normalizeAnalyticsText({ value })?.toLowerCase();

  if (normalizedValue === "paypal" || normalizedValue === "pay pal") {
    return "PayPal";
  }

  if (normalizedValue === "wire") {
    return "Wire";
  }

  return null;
}

function normalizeAnalyticsBonusTier({
  value,
}: {
  value: StatusTier | string | null | undefined;
}): string | null {
  if (typeof value === "string") {
    return normalizeAnalyticsText({ value });
  }

  return normalizeAnalyticsText({ value: value?.name });
}

function getFirstNormalizedString({
  values,
  normalizer = normalizeAnalyticsText,
}: {
  values: unknown[];
  normalizer?: ({ value }: { value: unknown }) => string | null;
}): string | null {
  for (const value of values) {
    const normalizedValue = normalizer({ value });
    if (normalizedValue !== null) {
      return normalizedValue;
    }
  }

  return null;
}

export function buildCustomerPortalAnalyticsPayload({
  event,
  customer,
  fallbackProfile,
  offerType,
  milesPointsType,
  amount,
  amount_of_miles,
  payoutMethod,
  region,
  bonusTier,
  dealCondition,
  dealStatus,
}: {
  event: CustomerPortalAnalyticsEventName;
  customer?: Customer | null;
  fallbackProfile?: AnalyticsProfileFallback;
  offerType: unknown;
  milesPointsType: unknown;
  amount: unknown;
  amount_of_miles: unknown;
  payoutMethod?: unknown;
  region?: unknown;
  bonusTier?: StatusTier | string | null;
  dealCondition?: CustomerPortalAnalyticsDealCondition | null;
  dealStatus?: "successful" | "rejected" | null;
}): CustomerPortalAnalyticsPayload {
  return {
    event,
    email: getFirstNormalizedString({
      values: [customer?.email, fallbackProfile?.email],
      normalizer: normalizeAnalyticsEmail,
    }),
    first_name: getFirstNormalizedString({
      values: [customer?.firstName, fallbackProfile?.firstName],
    }),
    last_name: getFirstNormalizedString({
      values: [customer?.lastName, fallbackProfile?.lastName],
    }),
    phone_number: getFirstNormalizedString({
      values: [customer?.phone, fallbackProfile?.phoneNumber],
      normalizer: normalizeAnalyticsPhoneNumber,
    }),
    offer_type: normalizeAnalyticsOfferType({ value: offerType }),
    miles_points_type: normalizeAnalyticsText({ value: milesPointsType }),
    amount: normalizeAnalyticsNumber({ value: amount }),
    amount_of_miles: normalizeAnalyticsNumber({ value: amount_of_miles }),
    currency: "USD",
    payout_method: normalizeAnalyticsPayoutMethod({ value: payoutMethod }),
    region: normalizeAnalyticsText({ value: region }),
    bonus_tier: normalizeAnalyticsBonusTier({ value: bonusTier }),
    deal_condition: dealCondition ?? null,
    deal_status: dealStatus ?? null,
  };
}

export function pushCustomerPortalAnalyticsEvent({
  payload,
}: {
  payload: CustomerPortalAnalyticsPayload;
}): CustomerPortalAnalyticsPayload {
  if (typeof window === "undefined") {
    return payload;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload as unknown as Record<string, unknown>);

  console.log("Analytics payload pushed:", payload);

  return payload;
}

export function savePendingDealSubmittedPayload({
  payload,
}: {
  payload: CustomerPortalAnalyticsPayload;
}): void {
  if (typeof window === "undefined") {
    return;
  }

  window.sessionStorage.setItem(
    PENDING_DEAL_SUBMITTED_PAYLOAD_STORAGE_KEY,
    JSON.stringify(payload),
  );
}

export function consumePendingDealSubmittedPayload():
  | CustomerPortalAnalyticsPayload
  | null {
  if (typeof window === "undefined") {
    return null;
  }

  const storedPayload = window.sessionStorage.getItem(
    PENDING_DEAL_SUBMITTED_PAYLOAD_STORAGE_KEY,
  );

  if (!storedPayload) {
    return null;
  }

  window.sessionStorage.removeItem(PENDING_DEAL_SUBMITTED_PAYLOAD_STORAGE_KEY);

  try {
    return JSON.parse(storedPayload) as CustomerPortalAnalyticsPayload;
  } catch (error) {
    console.error("Failed to parse pending deal submitted payload:", error);
    return null;
  }
}
